/**
 * The front page.
 *
 * It answers three questions in order, because that is the order a person
 * actually has them in: what did Congress just do, how divided were they
 * about it, and what subject do I care about.
 *
 * The number it leads with is not the flattering one. Of the 743 laws
 * passed, 408 went through on a voice vote with nobody on record -- and
 * saying that on the front page is the honest version of a site whose whole
 * pitch is telling you who voted for what.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type Landscape } from "../api";
import { useAsync } from "../useAsync";
import { toneFor } from "../bands";
import { LawCard } from "../components/LawCard";
import { Problem, Spinner } from "../components/bits";

export function Home() {
  const landscape = useAsync(() => api.landscape(), []);
  const recent = useAsync(() => api.laws({ per_page: 6, summarised_only: true }), []);
  const topics = useAsync(() => api.policyAreas(), []);

  return (
    <>
      <Hero total={landscape.data?.laws ?? null} />

      <div className="mx-auto max-w-6xl px-5">
        <section className="pb-4" aria-labelledby="divided">
          <h2 id="divided" className="font-display text-2xl font-semibold">
            How divided was Congress?
          </h2>
          <p className="mt-1.5 max-w-2xl text-muted">
            Every law that got a recorded vote, sorted by how far apart the two parties were on
            final passage.
          </p>

          {landscape.error && <div className="mt-6"><Problem error={landscape.error} retry={landscape.reload} /></div>}
          {landscape.loading && <Spinner label="Counting the votes…" />}
          {landscape.data && <DividedPanel data={landscape.data} />}
        </section>

        <section className="pt-14" aria-labelledby="recent">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 id="recent" className="font-display text-2xl font-semibold">
                The latest laws
              </h2>
              <p className="mt-1.5 text-muted">Most recently enacted first.</p>
            </div>
            <Link
              to="/laws"
              className="shrink-0 text-sm font-medium text-brand hover:underline"
            >
              Browse all →
            </Link>
          </div>

          {recent.error && <div className="mt-6"><Problem error={recent.error} retry={recent.reload} /></div>}
          {recent.loading && <Spinner label="Fetching the newest laws…" />}
          {recent.data && (
            <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {recent.data.laws.map((law) => (
                <LawCard key={law.bill_id} law={law} />
              ))}
            </div>
          )}
        </section>

        <section className="pt-16" aria-labelledby="topics">
          <h2 id="topics" className="font-display text-2xl font-semibold">
            Find the ones you care about
          </h2>
          <p className="mt-1.5 text-muted">Congress's own subject headings, by laws passed.</p>

          {topics.data && (
            <div className="mt-6 flex flex-wrap gap-2.5">
              {topics.data.data
                .filter((t) => t.laws > 0)
                .sort((a, b) => b.laws - a.laws)
                .slice(0, 18)
                .map((topic) => (
                  <Link
                    key={topic.policy_area}
                    to={`/laws?topic=${encodeURIComponent(topic.policy_area)}`}
                    className="group flex items-center gap-2 rounded-full border border-line bg-card px-4 py-2 text-sm transition hover:border-ink/25 hover:shadow-sm"
                  >
                    <span className="font-medium">{topic.policy_area}</span>
                    <span className="tnum text-xs text-faint group-hover:text-brand">
                      {topic.laws}
                    </span>
                  </Link>
                ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Hero({ total }: { total: number | null }) {
  const [term, setTerm] = useState("");
  const navigate = useNavigate();

  return (
    <section className="border-b border-line bg-gradient-to-b from-brand-soft/60 to-paper">
      <div className="mx-auto max-w-4xl px-5 py-20 text-center sm:py-28">
        <h1 className="font-display text-4xl leading-[1.1] font-semibold text-balance sm:text-6xl">
          Congress passed{" "}
          <span className="tnum text-brand">{total === null ? "743" : total.toLocaleString()}</span>{" "}
          laws.
          <br />
          Here is what they actually do.
        </h1>

        <p className="mx-auto mt-6 max-w-xl text-lg text-pretty text-muted">
          Every law since 2021, explained in plain English from its own text — and the roll call
          that shows exactly who voted for it.
        </p>

        <form
          className="mx-auto mt-9 flex max-w-xl gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            navigate(term.trim() ? `/laws?q=${encodeURIComponent(term.trim())}` : "/laws");
          }}
        >
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            type="search"
            aria-label="Search laws"
            placeholder="Search for a law — try “wildfire” or “health”"
            className="min-w-0 flex-1 rounded-xl border border-line bg-card px-5 py-3.5 text-base shadow-sm placeholder:text-faint"
          />
          <button
            type="submit"
            className="rounded-xl bg-ink px-6 py-3.5 font-medium text-white transition hover:bg-ink/85"
          >
            Search
          </button>
        </form>

        {/* Laws are the finished half. Everything a reader might actually
            still influence is in the other one, so it needs a way in from
            the front door and not only from the nav. */}
        <p className="mt-6 text-sm text-muted">
          Or see{" "}
          <Link to="/bills" className="font-medium text-brand hover:underline">
            what Congress is working on now
          </Link>{" "}
          — and how close each bill is to passing.
        </p>
      </div>
    </section>
  );
}

/** The vote landscape. */
export function DividedPanel({ data }: { data: Landscape }) {
  const most = Math.max(...data.buckets.map((b) => b.laws), 1);

  return (
    <>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.buckets.map((bucket) => {
          const tone = toneFor(bucket.key);
          return (
            <Link
              key={bucket.key}
              to={`/divided/${bucket.key}`}
              className="group rounded-2xl border border-line bg-card p-5 transition hover:border-ink/25 hover:shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]"
            >
              <div className={`tnum font-display text-4xl font-semibold ${tone.text}`}>
                {bucket.laws}
              </div>
              <p className="mt-1 font-medium">{bucket.label}</p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-sunk">
                <div
                  className={`h-full ${tone.bar}`}
                  style={{ width: `${(bucket.laws / most) * 100}%` }}
                />
              </div>
              {/* Named per tile rather than a bare "see all", so the link
                  reads on its own out of context -- which is how a screen
                  reader's list of links presents it. */}
              <p className="mt-3 text-sm font-medium text-muted group-hover:text-brand">
                See {bucket.laws === 1 ? "the law" : `all ${bucket.laws}`} →
              </p>
            </Link>
          );
        })}
      </div>

      <div className="mt-5 rounded-2xl border border-line bg-sunk/60 p-5 sm:flex sm:items-center sm:gap-5">
        <p className="tnum font-display text-3xl font-semibold">{data.voice}</p>
        <p className="mt-1 text-muted sm:mt-0">
          <span className="font-medium text-ink">
            of the {data.laws} laws passed with nobody on record.
          </span>{" "}
          They went through on a voice vote or by unanimous consent, so there is no roll call to
          show — which is not the same as everyone agreeing.
        </p>
      </div>

      {data.contested.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-lg font-semibold">The most divisive of them</h3>
          <p className="mt-1 text-sm text-muted">
            Laws where one party voted for and the other against, almost to a member.
          </p>
          <ul className="mt-4 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
            {data.contested.slice(0, 6).map((law) => (
              <li key={law.bill_id}>
                <Link
                  to={`/law/${law.bill_id}`}
                  className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-sunk/50"
                >
                  <span className="min-w-0 flex-1">
                    {/* What the law does, not what the bill it rode in on was
                        called. On a bill used as a vehicle the statute name
                        describes a law that was never enacted, and this list
                        is where most readers meet these six. */}
                    <span className="block truncate font-medium">
                      {law.plain_title || law.title}
                    </span>
                    {law.plain_title && (
                      <span className="block truncate text-xs text-faint">
                        Officially: {law.title}
                      </span>
                    )}
                    <span className="text-xs text-faint capitalize">
                      {law.chamber} · {law.law_number ? `Public Law ${law.law_number}` : law.bill_id}
                    </span>
                  </span>
                  <span className="hidden w-40 shrink-0 sm:block">
                    <ContestBar parties={law.parties} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** Share of each party that voted yes, as two short bars. */
function ContestBar({ parties }: { parties: Record<string, number> }) {
  const rows: [string, string][] = [
    ["D", "bg-dem"],
    ["R", "bg-gop"],
  ];
  return (
    <span className="block space-y-1">
      {rows.map(([party, colour]) => (
        <span key={party} className="flex items-center gap-2">
          <span className="w-3 text-[10px] font-semibold text-faint">{party}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-sunk">
            <span
              className={`block h-full ${colour}`}
              style={{ width: `${(parties[party] ?? 0) * 100}%` }}
            />
          </span>
          <span className="tnum w-8 text-right text-[10px] text-faint">
            {Math.round((parties[party] ?? 0) * 100)}%
          </span>
        </span>
      ))}
    </span>
  );
}
